/* mcLibrary.js — Module Configurator (mcv3) data + per-module config model.
 * Ported VERBATIM from lhc-app.html:
 *   MC_LIBRARY 51012–51124, mcEnsureConfig 51631–51649, mcModuleDimensions 51659–51667,
 *   mcRenderPropsTab config seeding 51764–51775, mcvGetTypeLabel 55333–55344,
 *   mcvGetPriceMultiplier 55346–55349, mcvCalcPrice 55351–55361,
 *   mcv3Render category list 56389–56399, _mcv3IsCustomised 56154–56173,
 *   _mcv3Icons 56138–56146, mcOpen stage gate 52632–52641.
 *
 * PARITY NOTES (byte-identical serialization):
 *  - mcEnsureConfig seeds p.config IN PLACE with the exact key INSERTION ORDER the
 *    prototype uses — the persisted _native snapshot JSON.stringify's placements
 *    wholesale, so key order must match an iframe save byte-for-byte.
 *  - mcEnsurePropsDefaults reproduces the mcRenderPropsTab seeding block (51764–51775):
 *    in the iframe this runs on EVERY selection render (plan/elevation), so merely
 *    selecting a cabinet seeds clearance/position/lockWidth/lockHeight/attributes.
 *  - All helpers are pure or mutate the passed placement only (engine shared-mutation
 *    semantics — no cloning).
 */
import { formatMoney } from "../../../lib/money";
import { svgDoorCount, svgDrawerCount, defDoorCount, defDrawerCount } from "../../engine/elevation";
import { effDef, isCustomSized, configOptionDeltas, moduleQuotePrice } from "../../engine/pricing";
import { getPricing, pgMultOf } from "../../engine/pricingSettings";
import { normalizeModel3d, materialSlotsOf } from "../../engine/modelResolver";

/* MC_LIBRARY — verbatim (51012–51124). */
export const MC_LIBRARY = {
  carcass: {
    colors: [
      { id: "c-white", label: "White", hex: "#F4F4F0" },
      { id: "c-grey", label: "Light Grey", hex: "#C9C9C5" },
      { id: "c-oak", label: "Light Oak", hex: "#C9A877" },
      { id: "c-walnut", label: "Walnut", hex: "#6B4B36" },
    ],
  },
  door: {
    types: [
      { id: "d-mel", label: "Melamine", desc: "Limited palette · Group 1" },
      { id: "d-mdf-mat", label: "MDF Lacquer Matte", desc: "Painted · Group 3" },
      { id: "d-mdf-shy", label: "MDF Lacquer Shiny", desc: "High-gloss · Group 4" },
      { id: "d-glass", label: "Glass Front", desc: "Tempered · Group 6" },
      { id: "d-ceramic", label: "Ceramic", desc: "Slim panel · Group 9" },
      { id: "d-veneer", label: "Wood Veneer", desc: "Oak / Walnut · Group 7" },
    ],
    colorsByType: {
      "d-mel": [
        { id: "mel-white", label: "White", hex: "#F4F4F0" },
        { id: "mel-cream", label: "Cream", hex: "#EDE5D4" },
        { id: "mel-grey", label: "Stone", hex: "#A8A8A4" },
      ],
      "d-mdf-mat": [
        { id: "mat-white", label: "Pure White", hex: "#F8F7F4" },
        { id: "mat-navy", label: "Navy", hex: "#2A3950" },
        { id: "mat-sage", label: "Sage", hex: "#8B9B7E" },
        { id: "mat-clay", label: "Clay", hex: "#B5896C" },
        { id: "mat-ink", label: "Ink", hex: "#1E2225" },
        { id: "mat-blush", label: "Blush", hex: "#D9B5A7" },
      ],
      "d-mdf-shy": [
        { id: "shy-white", label: "White Hi", hex: "#FFFFFF" },
        { id: "shy-ferr", label: "Ferrari Red", hex: "#C8202C" },
        { id: "shy-black", label: "Black Hi", hex: "#0E0E10" },
      ],
      "d-glass": [
        { id: "gl-clear", label: "Clear", hex: "#E5EEF2" },
        { id: "gl-smoke", label: "Smoke", hex: "#3A4046" },
        { id: "gl-bronze", label: "Bronze", hex: "#7A5D3A" },
      ],
      "d-ceramic": [
        { id: "cer-stat", label: "Statuario", hex: "#EDEAE2" },
        { id: "cer-piet", label: "Pietra", hex: "#5E5E59" },
        { id: "cer-onyx", label: "Onyx", hex: "#1B1816" },
      ],
      "d-veneer": [
        { id: "vn-oak", label: "Oak", hex: "#C9A877" },
        { id: "vn-walnut", label: "Walnut", hex: "#6B4B36" },
        { id: "vn-smoked", label: "Smoked Oak", hex: "#5A4634" },
      ],
    },
  },
  handle: {
    types: [
      { id: "h-bar", label: "Bar Pull" },
      { id: "h-knob", label: "Knob" },
      { id: "h-edge", label: "Edge Pull (J-pull)" },
      { id: "h-tab", label: "Tab" },
      { id: "h-push", label: "Push-to-Open (none)" },
      { id: "h-integ", label: "Integrated" },
    ],
    colors: [
      { id: "hc-steel", label: "Brushed Steel", hex: "#B8BCC0" },
      { id: "hc-black", label: "Matte Black", hex: "#1B1B1D" },
      { id: "hc-brass", label: "Brass", hex: "#B8915A" },
      { id: "hc-chrome", label: "Chrome", hex: "#D8D8DA" },
      { id: "hc-champ", label: "Champagne", hex: "#C5B59A" },
    ],
  },
  plinth: {
    materials: [
      { id: "p-pvc", label: "PVC" },
      { id: "p-mdf", label: "MDF" },
      { id: "p-stainless", label: "Stainless Steel" },
      { id: "p-match", label: "Match Door" },
    ],
    colors: [
      { id: "pc-black", label: "Black", hex: "#1B1B1D" },
      { id: "pc-grey", label: "Grey", hex: "#888A8C" },
      { id: "pc-white", label: "White", hex: "#F4F4F0" },
      { id: "pc-stain", label: "Stainless", hex: "#B8BCC0" },
    ],
  },
  lighting: {
    types: [
      { id: "l-none", label: "No lighting" },
      { id: "l-under", label: "Under-cabinet strip" },
      { id: "l-in", label: "In-cabinet (motion)" },
      { id: "l-toe", label: "Toe-kick (plinth) glow" },
    ],
    temps: [
      { id: "t-warm", label: "Warm white (2700K)" },
      { id: "t-neutral", label: "Neutral (4000K)" },
      { id: "t-cool", label: "Cool (5500K)" },
    ],
  },
  drawer: {
    runners: [
      { id: "dr-std", label: "Standard", desc: "Basic undermount · Group 1" },
      { id: "dr-legrabox", label: "Blum Legrabox", desc: "Full extension · soft-close · Group 3" },
      { id: "dr-tandembox", label: "Blum Tandembox Antaro", desc: "Full extension · soft-close · Group 2" },
      { id: "dr-movento", label: "Blum Movento", desc: "Heavy duty · push-to-open option · Group 3" },
    ],
    colors: [
      { id: "drc-white", label: "White", hex: "#F4F4F0" },
      { id: "drc-grey", label: "Silk Grey", hex: "#C9C9C5" },
      { id: "drc-black", label: "Orion Grey", hex: "#3A3A3C" },
      { id: "drc-stain", label: "Stainless", hex: "#B8BCC0" },
    ],
  },
};

/** mcEnsureConfig (51631) — seed p.config in place, EXACT key insertion order. */
export function mcEnsureConfig(p) {
  if (!p) return null;
  if (!p.config) {
    p.config = {
      carcass: "c-white",
      door: "d-mdf-mat",
      doorColor: "mat-white",
      handle: "h-bar",
      handleColor: "hc-steel",
      drawerRunner: "dr-std",
      drawerRunnerColor: "drc-white",
      plinth: { material: "p-pvc", color: "pc-black", height: 100, recess: 20 },
      lighting: { type: "l-none", temp: "t-warm" },
    };
  }
  if (!p.config.drawerRunner) p.config.drawerRunner = "dr-std";
  if (!p.config.drawerRunnerColor) p.config.drawerRunnerColor = "drc-white";
  return p.config;
}

/** mcRenderPropsTab seeding block (51764–51775) — runs on every selection render in the
 *  iframe (any view mode), so selecting a cabinet alone seeds these keys. Order matters. */
export function mcEnsurePropsDefaults(p) {
  const cfg = mcEnsureConfig(p);
  if (!cfg) return null;
  if (!cfg.clearance) cfg.clearance = { left: 0, right: 0 };
  if (!cfg.position) cfg.position = { angle: 0, outset: 0, elevation: 0 };
  if (cfg.lockWidth === undefined) cfg.lockWidth = true;
  if (cfg.lockHeight === undefined) cfg.lockHeight = true;
  if (!cfg.attributes) cfg.attributes = {
    fridgeBase: false, sinkBase: false, catene: false,
    changeTopConn: false, changeUbConn: false, dividerSplitBack: false,
    doorReveals: false, fixedShelfSplitBack: false, minifixUb: false,
    partitionSplitCase: false, applyAirflow: false, doubleShelf: true,
    miterCase: false, applyVerticalGola: false, hangingType: "none",
  };
  return cfg;
}

/** mcModuleDimensions (51659) → { text, custom } (the CUSTOM marker rendered by the caller). */
export function mcModuleDimensions(p, lib) {
  const def = effDef(p, lib || {});
  if (!def) return null;
  const w = Math.round(def.width * 1000);
  const h = Math.round(def.height * 1000);
  const d = Math.round(def.depth * 1000);
  return { text: `${w} × ${h} × ${d} mm`, custom: isCustomSized(p) };
}

/** mcvGetTypeLabel (55333). */
export function mcvGetTypeLabel(type) {
  if (!type) return "Module";
  const t = type.toUpperCase();
  if (t.startsWith("BU-")) return "Base Cabinet";
  if (t.startsWith("WU-")) return "Wall Cabinet";
  if (t.startsWith("TU-")) return "Tall Unit";
  if (t.startsWith("IS-")) return "Island";
  if (t.startsWith("CB-")) return "Corner Base";
  if (t.startsWith("DR-")) return "Drawer Unit";
  if (t.startsWith("CO-")) return "Column";
  return "Module";
}

/** mcvGetPriceMultiplier — CONSOLIDATED (2026-08-04 pricing-unification):
 *  the prototype's foot carried its OWN map (G2 1.15 / G3 1.35) that silently
 *  disagreed with the quote engine's PG_MULT (1.18 / 1.38) — reachable
 *  whenever no wizard was passed, i.e. two prices for one configuration.
 *  There is ONE multiplier table on this platform; this reads it. */
export function mcvGetPriceMultiplier(group) {
  return pgMultOf(group);
}

/** mcvCalcPrice (55351) — '€ 1.234,56' (de-DE).
 *  2026-07-22 mandate ("never different prices for the same configuration"):
 *  with `wizard` the panel shows EXACTLY the quote's per-unit price
 *  (moduleQuotePrice = price-group base + custom surcharge + technician option
 *  deltas). The legacy G1×multiplier path remains only for callers without a
 *  wizard (prototype parity). */
export function mcvCalcPrice(p, lib, blob, wizard) {
  let total;
  if (wizard) {
    total = moduleQuotePrice(p, lib, blob, wizard);
  } else {
    const def = (lib && p && lib[p.type]) || {};
    const base = def.price || 0;
    const mult = mcvGetPriceMultiplier(p && p.priceGroup);
    total = Math.round(base * mult);
    // same formula as the quote path (base + round(base*pct)) — the old
    // multiplicative form drifted by sub-euro amounts on odd bases
    if (isCustomSized(p)) total += Math.round(total * (getPricing().customSizePct / 100));
    if (blob && p && p.config) {
      const eff = effDef(p, lib) || def;
      configOptionDeltas(blob, eff, p.config).forEach((d) => { total += d.val; });
    }
  }
  return formatMoney(total);
}

/** mcv3Render category list (56389–56399): left icon rail entries for this module. */
export function mcv3Cats(p, lib, MCL) {
  const def = (lib && lib[p.type]) || {};
  const code = (def.code || p.type || "").toUpperCase();
  const hasDoors = defDoorCount(def, code) > 0;
  const hasDrawers = defDrawerCount(def, code) > 0;
  const isBase = /^(BU-|IS-|CB-|DR-)/.test(code);
  /* FLOOR-STANDING (2026-08-15 user report: "plinth is missing here" on TU-01).
   * A plinth belongs to anything that stands on the floor — base units AND
   * tall/column units — not to a SKU-prefix whitelist. Worktop/backsplash use
   * the same test, so a WALL unit can no longer be given (and charged for) a
   * worktop that never renders. */
  const role = def.role || "";
  const onFloor = isBase || role === "cabinet-base" || role === "cabinet-tall"
    || /^(TU-|CO-|CL-)/.test(code);
  const isWallHung = role === "cabinet-wall" || role === "cabinet-stack" || role === "cabinet-bridge";

  /* 2026-08-10 user mandate ("the panel must come from cabinet technician,
   * not random"): the RAIL mirrors the Products page's "Components & allowed
   * options" row for THIS product. A family the technician left empty (or
   * disallowed for this SKU) gets no tab — clicking a tool must never open a
   * screen of options the module cannot actually have. Without an option
   * library (no blob / catalog fetch failed) the legacy rail is kept, so the
   * panel still works offline. */
  const M = MCL || null;
  const n = (a) => (Array.isArray(a) ? a.length : 0);
  /* FURNITURE (batch 14, 2026-10-03): a sofa, armchair, lounger or outdoor piece is not a
   * cabinet — its rail is the FABRIC (Fabric Library, the uploaded photo becomes the
   * upholstery), material slots when the technician declared any, lighting when offered,
   * and the attributes. No carcass / fronts / handle / plinth / cuts tabs that change nothing. */
  const fam = String(def.family || "kitchens").toLowerCase();
  if (fam === "indoor" || fam === "outdoor") {
    const fc = [];
    if (n(M && M.fabric && M.fabric.zones)) fc.push({ id: "fabric", label: "Fabric" });
    const effF = effDef(p, lib) || def;
    if (Object.keys(materialSlotsOf(normalizeModel3d(effF))).length) fc.push({ id: "materials", label: "Materials" });
    if (M && M.lighting && n(M.lighting.types) > 1) fc.push({ id: "lighting", label: "Lighting" });
    fc.push({ id: "attributes", label: "Attributes" });
    return fc;
  }
  // "off" ONLY when the library DEFINES the family and it is empty — a library
  // that simply lacks the section (the static fallback) keeps the legacy tab.
  const off = (fam, key) => !!(M && fam) && n(fam[key]) === 0;

  const cats = [{ id: "carcass", label: "Carcass" }];
  if (hasDoors && !off(M && M.door, "types")) {
    cats.push({ id: "door", label: "Fronts" });   // 2026-08-15 user: "its door it needs to be FRONTS"
    cats.push({ id: "opening", label: "Opening" });
  }
  // MATERIAL SLOTS (Stage 3b): the tab appears ONLY when the technician gave
  // this module material slots — no slots, no tab (never an empty screen).
  const effM = effDef(p, lib) || def;
  if (Object.keys(materialSlotsOf(normalizeModel3d(effM))).length) cats.push({ id: "materials", label: "Materials" });
  if (!off(M && M.handle, "types")) cats.push({ id: "handle", label: "Handle" });
  if (n(M && M.gola && M.gola.options)) cats.push({ id: "gola", label: "Gola" });
  if (hasDrawers && !off(M && M.drawer, "runners")) cats.push({ id: "drawer", label: "Drawer" });
  if (onFloor && !isWallHung && !off(M && M.plinth, "materials")) cats.push({ id: "plinth", label: "Plinth" });
  // EQUIPMENT (2026-08-18, "put it below Plinth"): the technician's appliance
  // categories (taps/hobs/ovens/hoods/fridges/dishwashers) — offered whenever
  // the allow-list-gated feed has anything for this module.
  if (n(M && M.equip && M.equip.cats)) cats.push({ id: "equipment", label: "Equipment" });
  // Worktop + Backsplash tabs REMOVED from the module editor (2026-08-18 user:
  // "remove worktop backsplash here bcs we have in Room") — the Room step is
  // the ONE place those are chosen; cfg.countertop/cfg.backsplash still work
  // (Room writes them per module), only the per-module pickers are gone.
  if (!off(M && M.interior, "cats")) cats.push({ id: "interior", label: "Interior" });
  if (!(M && M.lighting) || n(M.lighting.types) > 1) cats.push({ id: "lighting", label: "Lighting" });
  cats.push({ id: "topcut", label: "Cuts" });            // 2026-07-22 v2: CAD cut/wall-fit engine
  cats.push({ id: "attributes", label: "Attributes" }); // v0.27: CabinetAttributes editor
  // SIDE PANELS (2026-08-29 user: "where we have lighting, cuts, attributes —
  // add it here below them"): offered whenever the technician uploaded any.
  if (n(M && M.sidePanel && M.sidePanel.options)) cats.push({ id: "sidepanel", label: "Side Panels" });
  return cats;
}

/** _mcv3IsCustomised (56154) — drives the rail dots. Prototype quirks preserved:
 *  plinth reads truthy `cfg.plinth.material` (seeded by mcEnsureConfig → dot shows once
 *  the panel has seeded defaults), countertop 'None' !== undefined → customised. */
export function mcv3IsCustomised(catId, p) {
  if (!p || !p.config) return false;
  const cfg = p.config;
  const tmp = { config: null };
  mcEnsureConfig(tmp);
  const defaults = tmp.config;
  switch (catId) {
    case "materials": return !!(cfg.materials && Object.keys(cfg.materials).length);
    case "carcass": return cfg.carcass !== defaults.carcass || cfg.countertop !== defaults.countertop;
    case "door": return cfg.door !== defaults.door || cfg.doorColor !== defaults.doorColor;
    case "opening": return cfg.hingeDir !== defaults.hingeDir || cfg.softClose !== defaults.softClose;
    case "handle": return cfg.handle !== defaults.handle || cfg.handleColor !== defaults.handleColor;
    case "drawer": return cfg.drawerRunner !== defaults.drawerRunner || cfg.drawerRunnerColor !== defaults.drawerRunnerColor;
    case "plinth": return !!((cfg.plinth && cfg.plinth.enabled === false) || (cfg.plinth && cfg.plinth.material));
    case "lighting": return !!(cfg.lighting && cfg.lighting.type && cfg.lighting.type !== "l-none");
    case "topcut": return !!((cfg.topCut && cfg.topCut.enabled) || (cfg.cuts && cfg.cuts.length));
    case "sidepanel": return !!(cfg.sidePanel && cfg.sidePanel !== "None");
    case "fabric": return !!(cfg.fabric && cfg.fabric !== "None");
    default: return false;
  }
}

/** _mcv3Icons (56138) — rail icon inner-SVG per category ('attributes' has none: empty svg). */
export const MCV3_ICONS = {
  carcass: '<rect x="3" y="7" width="18" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>',
  door: '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/>',
  opening: '<path d="M18 11V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v7"/><path d="M3 15h18M3 19h18"/><circle cx="12" cy="17" r="1"/>',
  handle: '<line x1="4" y1="12" x2="20" y2="12"/><circle cx="4" cy="12" r="2"/><circle cx="20" cy="12" r="2"/>',
  drawer: '<rect x="3" y="9" width="18" height="6" rx="1"/><line x1="7" y1="12" x2="17" y2="12"/>',
  plinth: '<rect x="2" y="17" width="20" height="4" rx="1"/><rect x="4" y="13" width="16" height="4" rx="1"/>',
  lighting: '<circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>',
  topcut: '<path d="M3 21V3h9v7h9v11z"/><path d="M12 3l9 7" stroke-dasharray="2 2"/>',
  materials: '<rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/>',
  gola: '<path d="M3 8h18v4a3 3 0 0 1-3 3H3z"/><line x1="3" y1="19" x2="21" y2="19"/>',
  sidepanel: '<rect x="4" y="4" width="4" height="16"/><rect x="11" y="6" width="9" height="12" rx="1"/>',
  equipment: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="4" x2="12" y2="7"/><line x1="12" y1="17" x2="12" y2="20"/><line x1="4" y1="12" x2="7" y2="12"/><line x1="17" y1="12" x2="20" y2="12"/>',
  worktop: '<rect x="2" y="8" width="20" height="3" rx="1"/><path d="M5 11v8M19 11v8"/>',
  backsplash: '<rect x="3" y="3" width="18" height="10" rx="1"/><path d="M3 8h18M9 3v10M15 3v10"/><line x1="2" y1="20" x2="22" y2="20"/>',
};

/** mcOpen stage gate (52632–52637): the panel only opens in cabinets/finishes stages.
 *  (Undefined stage → open, matching the prototype's falsy-stage bypass.) */
export function mcStageAllows(stage) {
  return !stage || stage === "cabinets" || stage === "finishes";
}

